import React, { useState } from 'react';
import ReportTable from './ReportTable';

const TABS = [
  { key: 'newBusiness', label: 'New Business', icon: '📈' },
  { key: 'cancellations', label: 'Cancellations', icon: '❌' },
  { key: 'pendingCancellations', label: 'Pending Cancellations', icon: '⚠️' },
  { key: 'pendingRenewals', label: 'Pending Renewals', icon: '🔄' },
  { key: 'pendingQuotes', label: 'Pending Quotes', icon: '📋' },
];

const COLUMN_DEFS = {
  newBusiness: [
    { key: 'insuredName', label: 'Insured' },
    { key: 'policyNumber', label: 'Policy #' },
    { key: 'lineOfBusiness', label: 'Line of Business' },
    { key: 'premium', label: 'Premium', format: 'currency' },
    { key: 'effectiveDate', label: 'Effective Date', format: 'date' },
    { key: 'producer', label: 'Producer' },
    { key: 'carrier', label: 'Carrier' },
    { key: 'status', label: 'Status', format: 'badge' },
  ],
  cancellations: [
    { key: 'insuredName', label: 'Insured' },
    { key: 'policyNumber', label: 'Policy #' },
    { key: 'lineOfBusiness', label: 'Line of Business' },
    { key: 'premium', label: 'Premium', format: 'currency' },
    { key: 'cancellationDate', label: 'Cancel Date', format: 'date' },
    { key: 'reason', label: 'Reason', format: 'badge' },
    { key: 'producer', label: 'Producer' },
    { key: 'carrier', label: 'Carrier' },
  ],
  pendingCancellations: [
    { key: 'insuredName', label: 'Insured' },
    { key: 'policyNumber', label: 'Policy #' },
    { key: 'lineOfBusiness', label: 'Line of Business' },
    { key: 'premium', label: 'Premium', format: 'currency' },
    { key: 'cancellationDate', label: 'Cancel Date', format: 'date' },
    { key: 'daysUntilCancel', label: 'Days Left', format: 'days' },
    { key: 'reason', label: 'Reason', format: 'badge' },
    { key: 'producer', label: 'Producer' },
    { key: 'carrier', label: 'Carrier' },
  ],
  pendingRenewals: [
    { key: 'insuredName', label: 'Insured' },
    { key: 'policyNumber', label: 'Policy #' },
    { key: 'lineOfBusiness', label: 'Line of Business' },
    { key: 'currentPremium', label: 'Current', format: 'currency' },
    { key: 'renewalPremium', label: 'Renewal', format: 'currency' },
    { key: 'changePercent', label: 'Change', format: 'percent' },
    { key: 'renewalDate', label: 'Renewal Date', format: 'date' },
    { key: 'producer', label: 'Producer' },
    { key: 'carrier', label: 'Carrier' },
    { key: 'status', label: 'Status', format: 'badge' },
  ],
  pendingQuotes: [
    { key: 'insuredName', label: 'Insured' },
    { key: 'lineOfBusiness', label: 'Line of Business' },
    { key: 'estimatedPremium', label: 'Est. Premium', format: 'currency' },
    { key: 'quoteDate', label: 'Quote Date', format: 'date' },
    { key: 'expiresDate', label: 'Expires', format: 'date' },
    { key: 'producer', label: 'Producer' },
    { key: 'carrier', label: 'Carrier' },
    { key: 'status', label: 'Status', format: 'badge' },
  ],
};

export default function Dashboard({ summary }) {
  const [activeTab, setActiveTab] = useState('newBusiness');
  const { kpis, reports } = summary;

  const kpiCards = [
    { label: 'New Business', value: kpis.newBusinessCount, sub: `$${kpis.newBusinessPremium.toLocaleString()} premium`, color: '#389e0d', bg: '#f6ffed', icon: '📈' },
    { label: 'Cancellations', value: kpis.cancellationCount, sub: `$${kpis.cancelledPremium.toLocaleString()} lost`, color: '#cf1322', bg: '#fff2f0', icon: '❌' },
    { label: 'Pending Cancellations', value: kpis.pendingCancellationCount, sub: 'Action required', color: '#d48806', bg: '#fffbe6', icon: '⚠️' },
    { label: 'Pending Renewals', value: kpis.pendingRenewalCount, sub: `$${kpis.pendingRenewalPremium.toLocaleString()} at risk`, color: '#096dd9', bg: '#e6f7ff', icon: '🔄' },
    { label: 'Pending Quotes', value: kpis.pendingQuoteCount, sub: `$${kpis.quotedPremium.toLocaleString()} pipeline`, color: '#722ed1', bg: '#f9f0ff', icon: '📋' },
    { label: 'Net Premium', value: `$${kpis.netPremiumChange.toLocaleString()}`, sub: kpis.netPremiumChange >= 0 ? 'Positive growth' : 'Net loss', color: kpis.netPremiumChange >= 0 ? '#389e0d' : '#cf1322', bg: kpis.netPremiumChange >= 0 ? '#f6ffed' : '#fff2f0', icon: '💰' },
  ];

  return (
    <div>
      {/* KPI Cards */}
      <div style={styles.kpiGrid}>
        {kpiCards.map((kpi, i) => (
          <div key={i} style={{ ...styles.kpiCard, borderLeft: `4px solid ${kpi.color}` }}>
            <div style={styles.kpiHeader}>
              <span style={styles.kpiIcon}>{kpi.icon}</span>
              <span style={styles.kpiLabel}>{kpi.label}</span>
            </div>
            <div style={{ ...styles.kpiValue, color: kpi.color }}>{kpi.value}</div>
            <div style={styles.kpiSub}>{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* Tab Navigation */}
      <div style={styles.tabBar}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            style={activeTab === tab.key ? { ...styles.tab, ...styles.tabActive } : styles.tab}
            onClick={() => setActiveTab(tab.key)}
          >
            <span>{tab.icon}</span> {tab.label}
            <span style={styles.tabCount}>{reports[tab.key]?.length || 0}</span>
          </button>
        ))}
      </div>

      {/* Report Table */}
      <div style={styles.tableCard}>
        <ReportTable
          data={reports[activeTab] || []}
          columns={COLUMN_DEFS[activeTab]}
        />
      </div>
    </div>
  );
}

const styles = {
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
  kpiCard: { background: '#fff', borderRadius: '10px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  kpiHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' },
  kpiIcon: { fontSize: '18px' },
  kpiLabel: { fontSize: '12px', fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' },
  kpiValue: { fontSize: '32px', fontWeight: 800, lineHeight: 1.1, marginBottom: '4px' },
  kpiSub: { fontSize: '12px', color: '#999' },
  tabBar: { display: 'flex', gap: '4px', marginBottom: '0', background: '#fff', borderRadius: '10px 10px 0 0', padding: '8px 8px 0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflowX: 'auto' },
  tab: { padding: '10px 18px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#666', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', transition: 'all 0.2s' },
  tabActive: { background: '#f0f2f5', color: '#1a1a2e', borderBottom: '2px solid #1a1a2e' },
  tabCount: { background: '#e8e8e8', color: '#666', padding: '1px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 700 },
  tableCard: { background: '#fff', borderRadius: '0 0 10px 10px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
};
